import {MsgOver, OSkinableInit} from "back/commons/basis";
import {IReg, IRegPointer, IUiEnv, REG} from "lib/commons/registry";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {GridColDef, IGridLineDrawer, IRowSortFn} from "back/commons/widgets/grid-core";
import {IBasicUniversePointer, ICoreUniverseEnv} from "lib/core/universe";
import {CellBuilderAccounts, OUsersGridInit, UsersGrid} from "back/core/widgets/usersGrid";
import {EUserAspects, EUserType, JUser, UsersSrv} from "lib/core/user";
import {BarActions, OBarActionsInit} from "back/commons/widgets/bars";
import {AccelKeyMgr, Action, ACTION, ActionWrapper, IAction} from "lib/commons/actions";
import {CreateUserAction, DeleteUsersAction, DisableUsersAction, IUsersActionCtx, UsersAction} from "back/core/actions/usersActions";
import {CellBuilderDate, CellBuilderIconLabel, CellBuilderString, GridDataRowJson} from "back/commons/widgets/grid-libs";
import {BaseAreaViewAsync, VIEWS} from "lib/commons/views";
import {POPUP} from "back/commons/widgets/popups";
import {AREAS, IArea, IControlAreaContext} from "lib/commons/areas";
import {IUserAreaContext} from "back/core/dialogs/userAreas";
import {FORMS} from "lib/commons/forms";
import {ERROR} from "lib/core/errorReport";
import "back/core/dialogs/dialogs_Perms";
import {USERSELF, UserSelfSrv} from "lib/core/userSelf";
import {GridDataRowJsonTree} from "back/commons/widgets/tree";
import {IRoleEditUiHandlerContext, RoleEditUiHandler, ROLES} from "lib/commons/roles";
import {OGridSmallInit} from "back/commons/widgets/grid-tags";
import Reg = REG.Reg;

export interface OUsersMgrInit extends OSkinableInit {
	reg: IReg<ICoreUniverseEnv>
	/** Init de la liste des ateliers. */
	usersGrid?: OUsersGridInit<IUsersActionCtx>
	uiContext?: "popup" | undefined
	/* Permet de contextualiser les messages UI */
	msgs?: JUsersMgrL10nMsgs
	/* account sélectionné au load  */
	preSelectAccount?: string
}

interface JUsersMgrL10nMsgs {
	noUsers?: string
	colRoles?: string
	colGroups?: string
}


abstract class UsersMgrBase<E extends IRegPointer<ICoreUniverseEnv & IUiEnv>> extends BaseAreaViewAsync<E> implements IGridLineDrawer, IUsersActionCtx, IUserAreaContext {
	reg: IReg<ICoreUniverseEnv & IUiEnv>;

	usersGridElt: UsersGrid<IUsersActionCtx>;

	msgs: JUsersMgrL10nMsgs = {};

	// IUserAreaContext
	uiContextSgn: string;

	userSelfSrv: UserSelfSrv
	usersSrv: UsersSrv;

	// IUsersActionCtx
	users?: JUser[] = null;

	// IControlLabelContext
	buildControlLabel = true;

	formUpdateElt: HTMLFormElement;
	forcedUserDataOnSave: JUser = {};

	protected _detailsBoxElt: HTMLDivElement;
	protected _listBoxElt: HTMLDivElement;
	protected _editUserToolbar: BarActions<UsersMgrBase<any>>;
	protected _showToolbarTop: BarActions<IUsersActionCtx>;
	protected _showToolbarBottom: BarActions<IUsersActionCtx>;
	protected _formUpdateEmptyData: Dict<any> = {};
	protected _detailUserCurrentData: Jsonisable;

	protected async _initialize(init: OUsersMgrInit): Promise<void> {
		if (!this.reg) this.reg = REG.createSubReg(this.findReg(init));
		this.usersSrv = this.reg.env.universe.adminUsers;
		this.userSelfSrv = this.reg.env.universe.userSelf;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("webzone:panel", sr);
		this.reg.installSkin("form-control-areas", sr);
		this.reg.installSkin("usersmgr-core", sr);
		this._initAndInstallSkin(this.localName, init);

		if (init.msgs) this.msgs = init.msgs;

		// Actions
		this.reg.addToList("actions:usersmgr:show:toolbar:top", "actionRefreshUi", 1, actionRefreshUi);

		// Accelkey
		let accelKeyMgr = new AccelKeyMgr().initFromMapActions(this.reg.mergeListsAsMap("accelkeys:usersmgr", "accelkeys:usersmgr:" + this.id));

		// Toolbar
		accelKeyMgr.initFromMapActions(this.reg.mergeListsAsMap("accelkeys:usersmgr", "accelkeys:usersmgr:" + this.id));

		// Tree
		let usersGridInit: OUsersGridInit<IUsersActionCtx> = Object.assign({
			reg: this.reg,
			usersSrv: this.usersSrv,
			displayRazFilterBtn: true,
			filterGroupsInputVisibility: true,
			filterRolesInputVisibility: true,
			disableAutoSearchOnInit: true,
			filterTxt: init.preSelectAccount,
			forceSelectEntry: init.preSelectAccount ? true : false,
			grid: Object.assign(init.usersGrid.grid, {
				selType: "multi",
				hideHeaders: false,
				columnDefs: this.reg.mergeLists("columns:usersmgr", "columns:usersmgr:" + this.id),
				skinOver: "usersmgr-core-tree",
				skinScroll: "scroll/small",
				lineDrawer: this,
				autoSelOnFocus: null,
				//defaultAction: init.defaultAction,
				//defaultActionCtx: this,
			} as OGridSmallInit)
		} as OUsersGridInit<IUsersActionCtx>, init.usersGrid);
		this._listBoxElt = sr.appendChild(<div id="list" c-resizable/>) as HTMLDivElement;
		sr.appendChild(<c-resizer/>);
		this._detailsBoxElt = sr.appendChild(<div id="details" c-resizable hidden/>) as HTMLDivElement;
		this.usersGridElt = this._listBoxElt.appendChild(new UsersGrid().initialize(usersGridInit)) as UsersGrid<IUsersActionCtx>;
		await this.usersGridElt.initializedAsync;

		this.usersGridElt.grid.ctxMenuActions = {
			actions: ACTION.injectSepByGroup(this.reg.mergeLists("actions:usersmgr", "actions:usersmgr:ctxtmenu", "actions:usersmgr:ctxtmenu:" + this.id, "actions:usersmgr:" + this.id) as IAction<IUsersActionCtx>[], this.reg.getPref("actions.usersmgr.groupOrder", "refresh users *"), this),
			actionContext: this,
		};

		this.usersGridElt.grid.addEventListener('keydown', async (ev: KeyboardEvent) => {
			return accelKeyMgr.handleKeyboardEvent(ev, this);
		});
		this.usersGridElt.grid.addEventListener('grid-select', async (ev: Event) => {
			let users = this.usersGridElt ? this.usersGridElt.getSelectedUsers() : null;
			this.users = users;
			this._showToolbarTop.refreshContent();
			this._showToolbarBottom.refreshContent();
			await this.showDetailsUser(users)
		});

		// Toolbars
		let toolbarsBox = this._listBoxElt.appendChild(<div id="toolbarsBox"/>);
		this._showToolbarTop = toolbarsBox.appendChild(new BarActions<IUsersActionCtx>().initialize({
			reg: this.reg,
			actions: ACTION.injectSepByGroup(
				this.reg.mergeLists("actions:usersmgr", "actions:usersmgr:show:toolbar:top", "actions:usersmgr:show:toolbar:top:" + this.id, "actions:usersmgr:" + this.id),
				this.reg.getPref("actions.usersmgr.groupOrder", "refresh users *"), this),
			uiContext: "bar",
			actionContext: this,
			disableFullOverlay: true,
		} as OBarActionsInit<IUsersActionCtx>));
		this._showToolbarTop.setAttribute("class", "showToolbarTop");

		this._showToolbarBottom = toolbarsBox.appendChild(new BarActions<IUsersActionCtx>().initialize({
			reg: this.reg,
			actions: ACTION.injectSepByGroup(
				this.reg.mergeLists("actions:usersmgr:show:toolbar:bottom", "actions:usersmgr:show:toolbar:bottom:" + this.id),
				this.reg.getPref("actions.usersmgr.groupOrder", "refresh users *"), this),
			uiContext: "bar",
			actionContext: this,
			disableFullOverlay: true,
		} as OBarActionsInit<IUsersActionCtx>));
		this._showToolbarBottom.setAttribute("class", "showToolbarBottom");


		// Form details
		this._editUserToolbar = this._detailsBoxElt.appendChild(new BarActions<UsersMgrBase<any>>().initialize({
			reg: this.reg,
			actions: ACTION.injectSepByGroup(
				this.reg.mergeLists("actions:usersmgr:edit:toolbar", "actions:usersmgr:edit:toolbar:" + this.id),
				this.reg.getPref("actions.usersmgr.groupOrder", "edit remove *"), this),
			uiContext: "bar",
			actionContext: this,
			disableFullOverlay: true,
		} as OBarActionsInit<UsersMgrBase<any>>));
		this._editUserToolbar.setAttribute("class", "detailToolbar");
		//this._editUserToolbar.setAttribute("hidden", "true");

		this.formUpdateElt = this._detailsBoxElt.appendChild(<form id="form" autocomplete="off"/>) as HTMLFormElement;
		//await this.initFormUi();

		let onFormUpdateRequest = 0;
		this.formUpdateElt.addEventListener('input', () => {
			if (onFormUpdateRequest) {
				clearTimeout(onFormUpdateRequest);
				onFormUpdateRequest = 0;
			}
			onFormUpdateRequest = setTimeout(async () => {
				await this.computeDirtyStatus();
				onFormUpdateRequest = 0;
			}, 300) as any;
		});
		this.formUpdateElt.addEventListener('keydown', async (ev: KeyboardEvent) => {
			if (ev.key === 's' && ACTION.isAccelPressed(ev)) {
				ev.preventDefault();
				ev.stopImmediatePropagation();
				UserFormSaveAction.SINGLETON_user.executeIfAvailable(this, ev);
				return true;
			}
			return false;
		});

		this.addEventListener('keydown', async (ev: KeyboardEvent) => {
			if (ev.key === 'Enter' && ACTION.isAccelPressed(ev)) {
				ev.preventDefault();
				ev.stopImmediatePropagation();
				if (init.uiContext === "popup" && await VIEWS.canHideView(this)) {
					POPUP.findPopupableParent(this).close(false);
				}
				return true;
			}
			return false;
		});

		/*
		this.reg.env.universe.auth.listeners.on("userUpdated", async (account: string, props?: JUser) => {
			await this.refresh();
		})
		*/


		await this.refresh();

	}

	/** Refresh UI */
	async _refresh() {
		this.refreshFreeze(1);
		try {
			super._refresh();
			await this.usersGridElt.doSearchUsers(undefined);
		} finally {
			this.refreshFreeze(-1);
		}
	}

	/**
	 * Formulaire
	 */
	/** Initialisation des fieldset du formulaire
	 * Retourne true si le user a été affiché
	 */
	async showDetailsUser(user: JUser[] | null, force?: boolean): Promise<boolean> {
		if (user === null || user.length == 0) {
			DOM.setHidden(this._detailsBoxElt, true);
			this._detailUserCurrentData = undefined;
			this.dirtyStatus = false;
			this.formUpdateElt.querySelectorAll("fieldset").forEach((fieldsetElt: HTMLFieldSetElement) => {
				fieldsetElt.disabled = false;
			});
			this._editUserToolbar.refreshContent();
			return false;
		} else if (user.length == 1) {
			DOM.setHidden(this._detailsBoxElt, false);
			await this.initFormUi('single');
			FORMS.jsonToForm(user[0], this.formUpdateElt, false, true);
			this._detailUserCurrentData = FORMS.formToJson(this.formUpdateElt);
			this.formUpdateElt.checkValidity();
			this.formUpdateElt.querySelectorAll("fieldset").forEach((fieldsetElt: HTMLFieldSetElement) => {
				fieldsetElt.disabled = this.isReadOnlyForm(user);
			});
			this.dirtyStatus = false;
			this._editUserToolbar.refreshContent();
			return true;
		} else {
			// sélection multiple de users
			DOM.setHidden(this._detailsBoxElt, false);
			await this.initFormUi('multi');
			FORMS.jsonToForm([], this.formUpdateElt, false, true);
			this._detailUserCurrentData = FORMS.formToJson(this.formUpdateElt);
			this.formUpdateElt.checkValidity();
			this.formUpdateElt.querySelectorAll("fieldset").forEach((fieldsetElt: HTMLFieldSetElement) => {
				fieldsetElt.disabled = this.isReadOnlyForm(user);
			});
			this.dirtyStatus = false;
			this._editUserToolbar.refreshContent();
			return true;
		}
	}

	/** Rafraichissements UI suite à modification des propriétés d'un user **/
	async onUserChange(user: JUser | null): Promise<void> {
		if (user === null) {
			//TODO : maj de tout le tree + déselection user courant
			await this.showDetailsUser(null);
		} else {
			// Maj du tree (si le user y est affiché)
			let datas = this.usersGridElt.dataHolder.getDatas();
			let userEntry = datas.find((entry) => entry.account === user.account);
			if (userEntry)
				this.usersGridElt.dataHolder.replaceRowKey(userEntry, user, false);
			// Maj du form user si c'est le user actuellement affiché
			if (this.users.length === 1 && this.users[0].account === user.account) {
				this.users = [user];
				await this.showDetailsUser(this.users);
			}
		}
	}

	/** Retourne true si le formulaire est readonly */
	protected isReadOnlyForm(user: JUser | JUser[] | null): boolean {
		if (!this.usersSrv.hasAspect(EUserAspects.updatable)) return true;
		if (Array.isArray(user)) {
			if (user.find((user) => user.isReadOnly)) return true;
		} else if (user !== null) {
			return user.isReadOnly;
		}
	}

	/** Reset/Initialisation du formulaire (def du/des fieldset)
	 * @param onlyIfNecessay : recalcul de la zone qu'en cas de changement de contexte ui
	 * */
	async initFormUi(uiContext: 'single' | 'multi' = 'single'): Promise<void> {
		// Recalcul de l'area : nécessaire car certaines areas dépendent du contexte (user courant, ...)
		this.initFormELt();
		let listCodes = uiContext == 'single' ? ["userProps:update:single", "userProps:update:single:" + this.id] : ["userProps:update:multi", "userProps:update:multi:" + this.id];
		await AREAS.applyLayout(this.formUpdateElt, await Promise.all(this.reg.mergeLists<IArea<IUserAreaContext & IControlAreaContext> | Promise<IArea<IUserAreaContext & IControlAreaContext>>>(...listCodes)), this, true);
		this._formUpdateEmptyData = FORMS.formToJson(this.formUpdateElt);
	}

	protected initFormELt(force?: boolean): void {
		if (!this.formUpdateElt.hasChildNodes() || force) {
			this.formUpdateElt.innerHTML = "";
			this.formUpdateElt.appendChild(
				<fieldset id="fieldset">
					<div id="common">
						<div class="fields" area-ids="account isDisabled enabledEndDt isHidden groups"/>
						<div class="fields" area-ids="nickNames firstName lastName groupName email"/>
					</div>
					<div class="fields" area-ids="*"/>
				</fieldset>);
		}
	}

	//private _lastFormUiContext: 'single' | 'multi' | undefined;

	/** Retourne true si au moins une valeur du formulaire a changé */
		// Etat dirty courant
	private _dirtyStatus: boolean = false;
	_dirtyMdgOver: MsgOver = <c-msg-over class="disableMsgOver"/> as MsgOver;

	get dirtyStatus(): boolean {return this._dirtyStatus}

	set dirtyStatus(val: boolean) {
		if (this._dirtyStatus != val) {
			this._dirtyStatus = val;
			//DOM.setHidden(this._editUserToolbar, !this._dirtyStatus);
			this._editUserToolbar.refreshContent();
			if (this.dirtyStatus == true) {
				this._dirtyMdgOver.showMsgOver(this._listBoxElt);
			} else {
				this._dirtyMdgOver.removeMsg();
			}
		}
	}

	async computeDirtyStatus(): Promise<boolean> {
		if (this._detailsBoxElt.hasAttribute("hidden"))
			return false;
		const userData = FORMS.formToJson(this.formUpdateElt);
		/*console.log("DIRTY :: ", JSON.stringify(wspData) !== JSON.stringify(this._lastWspData));
		console.log("LAST :: ", JSON.stringify(this._lastWspData));
		console.log("NEW  :: ", JSON.stringify(wspData));*/
		this.dirtyStatus = JSON.stringify(userData) !== JSON.stringify(this._detailUserCurrentData);
		return this.dirtyStatus;
	}

	/**
	 * Tree
	 */
	redrawLine(row: GridDataRowJson<JUser>, line: HTMLElement): void {}

	/**
	 * IViewApi
	 */
	onViewBeforeHide(close?: boolean): boolean {
		return false;
	}

	async onViewWaitForHide(close?: boolean): Promise<boolean> {
		return !await this.computeDirtyStatus() || await POPUP.confirm(`Des propriétés n'ont pas été enregistrées. Voulez-vous abandonner ces modifications et quitter ?`, this.reg.env.uiRoot, {
			okLbl: "Abandonner les modifications et quitter",
			cancelLbl: "Reprendre l'édition"
		});
	}

}

REG.reg.registerSkin('usersmgr-core', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
		overflow: hidden;
	}

	#list {
		display: flex;
		flex: 1;
		overflow: auto;
		min-height: 30%;
	}

	server-users-grid, c-input-ordered-set-panel {
		flex: 1;
	}

	#toolbarsBox {
		display: flex;
		flex-direction: column;
	}

	c-bar-actions.showToolbarTop {
		flex: 1;
	}

	c-bar-actions.showToolbarTop,
	c-bar-actions.showToolbarBottom {
		flex-direction: column;
		border-inline-start: 1px solid var(--border-color);
	}


	c-bar-actions.detailToolbar {
		flex-direction: row;
		border-bottom: 1px solid var(--border-color);
	}

	c-bar-actions.detailToolbar[hidden] {
		display: none;
	}

	c-resizer {
		pointer-events: auto;
		cursor: ns-resize;
		height: 2px;
	}

	.disableMsgOver {
		background-color: rgba(0, 0, 0, .2);
	}

	/* Form */
	#details {
		display: flex;
		flex-direction: column;
		overflow: auto;
		height: 60%;
	}

	#details[hidden] {
		display: none;
	}

	form {
		flex-direction: column;
		padding: 1em;
		overflow: auto;
	}

	fieldset {
		border: none;
		padding: 0;
		margin: 0;
		min-width: 0;
	}

	.lbl {
		min-width: 4em;
	}

	#common {
		display: flex;
		flex: 1;
		min-height: 0;
		min-width: 0;
		flex-wrap: wrap;
	}

	#common > .fields {
		flex: 1;
		display: grid;
		min-width: 10em;
		grid-template-columns: auto 1fr;
		grid-row-gap: .7rem;
		grid-auto-rows: max-content;
	}

	#common > .fields > div {
		display: contents;
	}

	#common > .fields:first-child {
		margin-inline-end: 2em;
	}

	.fields div[hidden] {
		display: none !important;
	}

	div[area-id="passwordSetMode"] > .subPanel {
		border: 0;
		max-width: fit-content;
	}

	.ctrlLbl > .ctrlLbl {
		margin: 0;
	}

	select {
		max-width: fit-content;
	}

`);
REG.reg.registerSkin('usersmgr-core-tree', 1, /* language=CSS */ `
	div[data-user-special] {
		/*opacity: 60%;*/
		font-style: italic;
	}

  div[data-user-hidden] {
	  /*font-style: italic;*/
  }

  div[data-user-current] > div.accountCell {
	  font-weight: bold;
  }

  /** Mise en forme de CellBuilderUserRoles */

  c-userref + c-userref::before {
	  content: ", ";
  }

  .role-unknown {
	  text-decoration: line-through;
  }

  .role-inherited {
	  font-style: italic;
  }

  .icon {
	  background-size: auto .9rem;
	}

	.accountCell {
		display: flex;
	}

	.accountLabel {
		flex: 1;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	.statusIcon {
		background-repeat: no-repeat;
		background-position: center;
		background-size: auto .7rem;
		padding-inline-start: 1rem;
	}

	.disabled {
		background-image: url(/@skin@/core/objects/user/disabled.svg);
	}

	.locked {
		background-image: url(/@skin@/core/objects/user/locked.svg);
	}

	.system {
		background-image: url(/@skin@/core/objects/user/system.svg);
	}

`);


export class UsersMgr<E extends IRegPointer<ICoreUniverseEnv>> extends UsersMgrBase<E> {
	protected async _initialize(init: OUsersMgrInit): Promise<void> {
		if (!this.reg) this.reg = REG.createSubReg(this.findReg(init));
		this.usersSrv = this.reg.env.universe.adminUsers;
		this.userSelfSrv = this.reg.env.universe.userSelf;
		this.id = "users";
		this.uiContextSgn = "@user#edit";
		this.forcedUserDataOnSave.userType = EUserType.user;
		let mgrInit: OUsersMgrInit = Object.assign({
			usersGrid: {
				firstMaxEntries: 500,
				fullMaxEntries: 5000,
				filterType: EUserType.user,
				filterHiddenInputVisibility: true,
				filterHiddenInputPerms: "ui.usersMgr.admin.hiddenUser",
				filterHidden: false,
				filterHiddenBtnInit: {
					title: "Afficher/masquer les comptes de service",
				},
				grid: {
					emptyBody: () => <span>{this.msgs.noUsers || "Aucun utilisateur"}</span>,
				}
			}
		}, init);
		// Toolbars
		this.reg.addToList("actions:usersmgr:show:toolbar:top:users", "actionCreateUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(CreateUserAction.SINGLETON_user));
		// ContextMenu
		this.reg.addToList("actions:usersmgr:ctxtmenu:users", "actionDeleteUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(DeleteUsersAction.SINGLETON_user));
		this.reg.addToList("actions:usersmgr:ctxtmenu:users", "actionEnableUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(DisableUsersAction.SINGLETON_user_enable));
		this.reg.addToList("actions:usersmgr:ctxtmenu:users", "actionDisableUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(DisableUsersAction.SINGLETON_user_disable));
		// Colonnes
		this.reg.addToList("columns:usersmgr:users", "account", 1, new GridColDef('account')
			.setLabel("Compte").setFlex("4rem", 1, 1)
			.setCellBuilder(new CellBuilderIconLabel("account").setIconWidth("1rem")
				//.override("_getIcon", (row: GridDataRowJsonTree<JUser>) =>  row.cacheHolder["isDisabled"] ? "/@skin@/core/objects/user-disable.svg" : "/@skin@/core/objects/user.svg")
				.override("_getIcon", (row: GridDataRowJsonTree<JUser>) => "/@skin@/core/objects/user.svg")
				.override("getIconFilter", (row: GridDataRowJsonTree<JUser>) => {
					const isHidden = row.cacheHolder["isHidden"];
					return isHidden ? "grayscale(100%)" : "";
				})
				.override("redrawCell", function (this: CellBuilderIconLabel, row: GridDataRowJsonTree<JUser>, root: HTMLElement) {
					const icon = this._getIcon(row);
					const label = this._getValue(row);
					DOM.addClass(root, "accountCell");
					root.innerText = "";
					// Icone
					const span = this._buildContent(row, root);
					if (icon) {
						span.style.unicodeBidi = 'isolate';
						span.style.backgroundImage = icon.startsWith('--') ? `var(${icon})` : `url("${icon}")`;
						span.style.filter = this.getIconFilter(row);
					} else {
						span.style.backgroundImage = 'none';
					}
					// label
					if (label) root.appendChild(<span class="accountLabel">{label}</span>);
					DOM.setAttr(root, "title", this._getDescription(row) || undefined);
					// Icones de statut
					if (row.rowDatas.isReadOnly)
						root.appendChild(<span class="statusIcon locked" title="Compte en lecture seule"/>);
					if (row.rowDatas.isDisabled)
						root.appendChild(<span class="statusIcon disabled" title="Compte désactivé"/>);
					if (row.rowDatas.isHidden)
						root.appendChild(<span class="statusIcon system" title="Compte de service"/>);
				})
			)
		);
		if (this.usersSrv.hasAspect(EUserAspects.oneNickName)) {
			this.reg.addToList("columns:usersmgr:users", "nickNames", 1, new GridColDef('nickNames')
				.setLabel("Pseudo").setFlex('2rem', 1, 1)
				.setCellBuilder(new CellBuilderString("nickNames")));
		}
		this.reg.addToList("columns:usersmgr:users", "firstName", 1, new GridColDef('firstName')
			.setLabel("Prénom").setFlex('2rem', 1, 1)
			.setCellBuilder(new CellBuilderString("firstName")));
		this.reg.addToList("columns:usersmgr:users", "lastName", 1, new GridColDef('lastName')
			.setLabel("Nom").setFlex('2rem', 1, 1)
			.setCellBuilder(new CellBuilderString("lastName")));
		if (this.usersSrv.hasAspect(EUserAspects.rolable)) {
			this.reg.addToList("columns:usersmgr:users", "roles", 1, new GridColDef('roles')
				.setLabel("Rôles").setDescription(this.msgs.colRoles || "Rôles résolus de l'utilisateur dans ce contexte").setFlex('5rem', 1, 1)
				.setCellBuilder(new CellBuilderUserRoles(this.reg, "roles")));
		}
		if (this.usersSrv.hasAspect(EUserAspects.groupable)) {
			this.reg.addToList("columns:usersmgr:users", "groups", 1, new GridColDef('groups')
				.setLabel("Groupes").setDescription(this.msgs.colGroups || "Groupes affectés à cet utilisateur").setFlex('5rem', 1, 1)
				.setCellBuilder(new CellBuilderAccounts(this.reg, "groups", true, false)));
		}
		this.reg.addToList("columns:usersmgr:users", "pwdDt", 1, new GridColDef('pwdDt')
			.setLabel("Chgt Mdp").setFlex('2rem', 1, 1).setDescription("Date du dernier changement de mot de passe")
			.setCellBuilder(new CellBuilderDate("pwdDt").setCellClass("center")));
		// Form details
		this.reg.addToList("actions:usersmgr:edit:toolbar:users", 'userFormSave', 1, UserFormSaveAction.SINGLETON_user);
		this.reg.addToList("actions:usersmgr:edit:toolbar:users", 'userFormRevert', 1, UserFormRevertAction.SINGLETON_user);
		this.reg.addToList("actions:usersmgr:show:toolbar:top:users", "actionDeleteUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(DeleteUsersAction.SINGLETON_user));
		this.reg.addSvcToList("userProps:update:single:users", 'account', 1, 'area.users.props');
		this.reg.addSvcToList("userProps:update:single:users", 'isDisabled', 1, 'area.users.isDisabled.users');
		if (this.usersSrv.hasAspect(EUserAspects.enabledEndDt)) this.reg.addSvcToList("userProps:update:single:users", 'enabledEndDt', 1, 'area.users.enabledEndDt');
		this.reg.addSvcToList("userProps:update:single:users", 'nickNames.first', 1, 'area.users.nickNames.first');
		this.reg.addSvcToList("userProps:update:single:users", 'isHidden', 1, 'area.users.isHidden.users');
		this.reg.addSvcToList("userProps:update:single:users", 'firstName', 1, 'area.users.firstName');
		this.reg.addSvcToList("userProps:update:single:users", 'lastName', 1, 'area.users.lastName');
		this.reg.addSvcToList("userProps:update:single:users", 'groups', 1, 'area.users.groupsmembers.users');
		this.reg.addSvcToList("userProps:update:single:users", 'grantedRoles', 1, 'area.users.grantedRoles.users');
		this.reg.addSvcToList("userProps:update:single:users", 'email', 1, 'area.users.email.user');
		this.reg.addSvcToList("userProps:update:multi:users", 'groups', 1, 'area.users.groupsmembers.multi.users');
		this.reg.addSvcToList("userProps:update:multi:users", 'grantedRoles', 1, 'area.users.grantedRoles.multi.users');


		await super._initialize(mgrInit);

		this._dirtyMdgOver.addEventListener("dblclick", (ev) => {UserFormRevertAction.SINGLETON_user.executeIfAvailable(this, ev)});
		this.formUpdateElt.addEventListener("keydown", (ev) => {
			if (ev.key === 'Escape' && this.dirtyStatus) {
				UserFormRevertAction.SINGLETON_user.executeIfAvailable(this, ev);
				ev.stopPropagation()
			}
		});
	}

	/**
	 * Tree
	 */
	redrawLine(row: GridDataRowJson<JUser>, line: HTMLElement): void {
		const reg = REG.findReg<IBasicUniversePointer>(line);
		super.redrawLine(row, line);
		const isCurrentUSer = row.rowDatas.account === reg.env.universe.auth.currentUser.account
		let propsStr = [];
		if (isCurrentUSer) propsStr.push("actuellement connecté");
		if (row.rowDatas.isDisabled) propsStr.push("désactivé");
		if (row.rowDatas.isHidden) propsStr.push("de service");
		if (row.rowDatas.isReadOnly) propsStr.push("en lecture seule");
		if (propsStr.length > 0) {
			let countProps = propsStr.join(", ");
			line.title = `Compte ${countProps}`;
			DOM.setAttrBool(line, "data-user-special", true);
		} else {
			delete line.title;
			line.removeAttribute("data-user-special");
		}
		DOM.setAttrBool(line, "data-user-current", isCurrentUSer);
	}

	isReadOnlyForm(user: JUser | JUser[] | null): boolean {
		if (!this.reg.hasPerm("action.users#update.user")) return true;
		return super.isReadOnlyForm(user);
	}
}

customElements.define("usersmgr-users", UsersMgr);
REG.reg.registerSkin('usersmgr-users', 1, /* language=CSS */ `
	.ctrlLbl[area-id=isHidden] > .lbl::before,
	.ctrlLbl[area-id=isDisabled] > .lbl::before {
		content: "";
		padding-inline-start: 1.2rem;
		background-repeat: no-repeat;
		background-position: left;
		background-size: auto .9rem;
		filter: grayscale(100%) opacity(.8);
	}

	.ctrlLbl[area-id=enabledEndDt] > .lbl {
	  text-align: end;
  }

  .ctrlLbl[area-id=isHidden] > .lbl::before {
	  background-image: url(/@skin@/core/objects/user/system.svg);
  }

  .ctrlLbl[area-id=isDisabled] > .lbl::before {
	  background-image: url(/@skin@/core/objects/user/disabled.svg);
  }

`);

export class GroupsMgr<E extends IRegPointer<ICoreUniverseEnv>> extends UsersMgrBase<E> {
	protected async _initialize(init: OUsersMgrInit): Promise<void> {
		if (!this.reg) this.reg = REG.createSubReg(this.findReg(init));
		this.usersSrv = this.reg.env.universe.adminUsers;
		this.userSelfSrv = this.reg.env.universe.userSelf;
		this.forcedUserDataOnSave.userType = EUserType.group;
		this.id = "groups";
		this.uiContextSgn = "@group#edit";
		let mgrInit: OUsersMgrInit = Object.assign({
			usersGrid: {
				firstMaxEntries: 500,
				fullMaxEntries: 5000,
				filterType: EUserType.group,
				filterGroupsInputVisibility: false,
				filterHiddenInputVisibility: false,
				filterHidden: null,
				grid: {
					emptyBody: () => <span>Aucun groupe</span>,
				}
			}
		}, init);
		// Toolbar
		this.reg.addToList("actions:usersmgr:show:toolbar:top:groups", "actionCreateUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(CreateUserAction.SINGLETON_group));
		// Colonnes
		this.reg.addToList("columns:usersmgr:groups", "account", 1, new GridColDef('account')
			.setLabel("Compte").setFlex("4rem", 1, 1)
			.setCellBuilder(new CellBuilderIconLabel("account").setIconWidth("1.2em")
				.override("_getIcon", (row: GridDataRowJsonTree<JUser>) => {
					return "/@skin@/core/objects/group.svg";
				}).override("getIconFilter", (row: GridDataRowJsonTree<JUser>) => {
					const isHidden = row.cacheHolder["isHidden"];
					return isHidden ? "grayscale(100%)" : "";
				})
			)
		);
		this.reg.addToList("columns:usersmgr:groups", "groupName", 1, new GridColDef('groupName')
			.setLabel("Intitulé").setFlex('4em', 1, 1)
			.setCellBuilder(new CellBuilderString("groupName")));
		if (this.usersSrv.hasAspect(EUserAspects.rolable)) {
			this.reg.addToList("columns:usersmgr:groups", "roles", 1, new GridColDef('roles')
				.setLabel("Rôles").setDescription("Rôles résolus du groupe dans ce contexte").setFlex('5rem', 1, 1)
				.setCellBuilder(new CellBuilderUserRoles(this.reg, "roles")));
		}
		// ContextMenu
		this.reg.addToList("actions:usersmgr:ctxtmenu:groups", "actionDeleteUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(DeleteUsersAction.SINGLETON_group));
		// usage ? this.reg.addToList("actions:usersmgr:ctxtmenu:groups", "actionEnableUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(DisableUsersAction.SINGLETON_group_enable));
		// usage ? this.reg.addToList("actions:usersmgr:ctxtmenu:groups", "actionDisableUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(DisableUsersAction.SINGLETON_group_disable));
		// Form details
		this.reg.addToList("actions:usersmgr:edit:toolbar:groups", 'userFormSave', 1, UserFormSaveAction.SINGLETON_group);
		this.reg.addToList("actions:usersmgr:edit:toolbar:groups", 'userFormRevert', 1, UserFormRevertAction.SINGLETON_group);
		this.reg.addToList("actions:usersmgr:show:toolbar:top:groups", "actionDeleteUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(DeleteUsersAction.SINGLETON_group));
		// usage ? this.reg.addToList("actions:usersmgr:edit:toolbar:groups", "actionEnableUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(DisableUsersAction.SINGLETON_group_enable));
		// usage ? this.reg.addToList("actions:usersmgr:edit:toolbar:groups", "actionDisableUser", 1, new ActionWrapperRefresUsersMgr(this).setOverridenSvc(DisableUsersAction.SINGLETON_group_disable));
		this.reg.addSvcToList("userProps:update:single:groups", 'account', 1, 'area.users.props');
		this.reg.addSvcToList("userProps:update:single:groups", 'groupName', 1, 'area.users.groupName');
		this.reg.addSvcToList("userProps:update:single:groups", 'isHidden', 1, 'area.users.isHidden.groups');
		this.reg.addSvcToList("userProps:update:single:groups", 'groups', 1, 'area.users.groupsmembers.groups');
		this.reg.addSvcToList("userProps:update:single:groups", 'grantedRoles', 1, 'area.users.grantedRoles.groups');
		this.reg.addSvcToList("userProps:update:single:groups", 'email', 1, 'area.users.email.group');
		// multi
		this.reg.addSvcToList("userProps:update:multi:groups", 'grantedRoles', 1, 'area.users.grantedRoles.multi.groups');

		await super._initialize(mgrInit);

		this._dirtyMdgOver.addEventListener("dblclick", (ev) => {UserFormRevertAction.SINGLETON_group.executeIfAvailable(this, ev)});
		this.formUpdateElt.addEventListener("keydown", (ev) => {
			if (ev.key === 'Escape' && this.dirtyStatus) {
				UserFormRevertAction.SINGLETON_group.executeIfAvailable(this, ev);
				ev.stopPropagation()
			}
		});
	}

	isReadOnlyForm(user: JUser | JUser[] | null): boolean {
		if (!this.reg.hasPerm("action.users#update.group")) return true;
		return super.isReadOnlyForm(user);
	}

	/**
	 * Tree
	 */
	redrawLine(row: GridDataRowJson<JUser>, line: HTMLElement): void {
		super.redrawLine(row, line);
		DOM.setAttrBool(line, "data-user-hidden", row.rowDatas.isHidden);
		line.title = row.rowDatas.isHidden ? "Visibilité : gestion" : "Visibilité : globale";
	}
}

customElements.define("usersmgr-groups", GroupsMgr);
REG.reg.registerSkin('usersmgr-groups', 1, /* language=CSS */ `
`);

/** CellBuilder **/

/** Afichage des rôles *résolus* d'un user */
export class CellBuilderUserRoles extends CellBuilderString {
	protected _rolesUiHandler: RoleEditUiHandler<IRoleEditUiHandlerContext>[];

	constructor(protected _reg: IReg<any>, public dataKey: string, protected rolesLists: string = ROLES.UI_ROLES_LIST) {
		super(dataKey);
	}

	getColSortFn(): IRowSortFn {
		// Comparaison uniquement sur le code : le titre des roles n'est résolu qu'en async sur l'affichage de la cellule...
		return (r1: GridDataRowJson<JUser>, r2: GridDataRowJson<JUser>) => {
			const r1_rolesStr = ROLES.resolveUserRoles({grantedRoles: r1.rowDatas.grantedRoles, refusedRoles: r1.rowDatas.refusedRoles, inheritedRoles: r1.rowDatas.inheritedRoles})?.join(",") || "";
			const r2_rolesStr = ROLES.resolveUserRoles({grantedRoles: r2.rowDatas.grantedRoles, refusedRoles: r2.rowDatas.refusedRoles, inheritedRoles: r2.rowDatas.inheritedRoles})?.join(",") || "";
			return r1_rolesStr.localeCompare(r2_rolesStr);

			/*
			const r1_rolesStr = (r1.cacheHolder["specifRolesResult"] instanceof Promise
				? ROLES.resolveUserRoles({grantedRoles: r1.rowDatas.grantedRoles, refusedRoles: r1.rowDatas.refusedRoles, inheritedRoles: r1.rowDatas.inheritedRoles})
				: r1.cacheHolder["specifRolesSort"])||"";
			const r2_rolesStr = (r2.cacheHolder["specifRolesResult"] instanceof Promise
				? ROLES.resolveUserRoles({grantedRoles: r2.rowDatas.grantedRoles, refusedRoles: r2.rowDatas.refusedRoles, inheritedRoles: r2.rowDatas.inheritedRoles})
				: r2.cacheHolder["specifRolesSort"])||"";
			return r1_rolesStr.localeCompare(r2_rolesStr);
			 */
		}
	}

	private async getRolesUiHandler(): Promise<RoleEditUiHandler<IRoleEditUiHandlerContext>[]> {
		if (this._rolesUiHandler !== undefined)
			return this._rolesUiHandler;
		else if (this.rolesLists)
			this._rolesUiHandler = await Promise.all(this._reg.mergeLists(this.rolesLists)) as RoleEditUiHandler<IRoleEditUiHandlerContext>[];
		return this._rolesUiHandler;
	}

	redrawCell(row: GridDataRowJson<JUser>, root: HTMLElement): void {

		let reg: Reg<ICoreUniverseEnv & IUiEnv> = REG.findReg(root) as Reg<ICoreUniverseEnv & IUiEnv>;
		// FIXME : contournement du fait que la row peut évoluer.
		const cacheHolder = row.cacheHolder;
		let cache: { sort: number, name: string, level: "set" | "inherited" | "unknown" }[] | Promise<{ sortUi: number, name: string, level: "set" | "inherited" | "unknown" }[]> = cacheHolder["specifRolesResult"];
		if (cache == null) {
			//1èr redraw pour cette row.
			let roles = ROLES.resolveUserRoles({grantedRoles: row.rowDatas.grantedRoles, refusedRoles: row.rowDatas.refusedRoles, inheritedRoles: row.rowDatas.inheritedRoles});
			let roleUiCtx: IRoleEditUiHandlerContext = {
				reg,
				grantedRoles: row.rowDatas.grantedRoles,
				refusedRoles: row.rowDatas.refusedRoles,
				inheritedRoles: row.rowDatas.inheritedRoles,
			}
			if (roles.length > 0) {
				// Affichage des roles en sync
				DOM.setTextContent(root, roles.join(", "));
				cacheHolder["specifRolesResult"] = cache = this.getRolesUiHandler().then(async (rolesUi: RoleEditUiHandler<IRoleEditUiHandlerContext>[]) => {
					if (cacheHolder["specifRolesResult"] !== cache) return; //raceCond : les données de cette row ont évoluées.
					const result = await ROLES.resolveRolesList(roles, roleUiCtx, rolesUi);
					if (cacheHolder["specifRolesResult"] !== cache) return; //raceCond : les données de cette row ont évoluées.
					return cacheHolder["specifRolesResult"] = result;
				});
			} else {
				cache = cacheHolder["specifRolesResult"] = null;
			}
		}

		if (cache instanceof Promise) {
			let roles = ROLES.resolveUserRoles({grantedRoles: row.rowDatas.grantedRoles, refusedRoles: row.rowDatas.refusedRoles, inheritedRoles: row.rowDatas.inheritedRoles});

			//cache non reseté mais calcul en cours, on attend, on réaffiche le nombre en attendant.
			DOM.setTextContent(root, roles.join(", "));
			(root as any).specifRolesPending = cache; //raceCond pour refresh async de la line HTML.
			cache.then(() => {
				if ((root as any).specifRolesPending !== cache) return; //raceCond : cette line HTML a été redessinée
				(root as any).specifRolesPending = null; //cleanup
				redrawCellUi(root, cacheHolder["specifRolesResult"]);
			});
		} else {
			(root as any).specifRolesPending = null; //raceCond pour refresh async de la line HTML.
			redrawCellUi(root, null);
		}

		function redrawCellUi(root: HTMLElement, newUiRoles?: { role: string, sortUi: number, name: string, level: "set" | "inherited" | "unknown" }[]) {
			if (newUiRoles && newUiRoles.length > 0) {
				//cacheHolder["specifRolesSort"] = "";
				cacheHolder["specifRolesContent"] = <div/>;
				cacheHolder["specifRolesTitle"] = newUiRoles.length > 1 ? "Rôles : " : "Rôle : ";
				newUiRoles.forEach((entry, pos) => {
					//cacheHolder["specifRolesSort"] += `,${entry.name}`;
					// - Content
					let hasNextEntry = pos < newUiRoles.length - 1;
					if (hasNextEntry)
						cacheHolder["specifRolesContent"].appendChild(<span class={"role-" + entry.level}>{entry.name}, </span>)
					else
						cacheHolder["specifRolesContent"].appendChild(<span class={"role-" + entry.level}>{entry.name}</span>)
					// - Title
					let roleName = entry.name;
					switch (entry.level) {
					case "inherited":
						cacheHolder["specifRolesTitle"] += (newUiRoles.length > 1 ? "\n- " : "") + `${roleName} (issu d'un autre niveau)`;
						break;
					case "unknown":
						cacheHolder["specifRolesTitle"] += (newUiRoles.length > 1 ? "\n- " : "") + `${roleName} (inconnu)`;
						break;
					default:
						cacheHolder["specifRolesTitle"] += (newUiRoles.length > 1 ? "\n- " : "") + roleName;
					}
				});
			}
			root.innerHTML = cacheHolder["specifRolesContent"] ? cacheHolder["specifRolesContent"].innerHTML : "";
			DOM.setAttr(root, "title", cacheHolder["specifRolesTitle"]);
		}
	}

}


/** Actions **/

/** action refresh UI */
const actionRefreshUi = new Action<any>("refreshUi")
	.setLabel("Rafraichir")
	.setGroup("refresh")
	.setIcon("/@skin@/commons/icons/refresh.svg")
	.setExecute(async function (this: Action<any>, ctx: any, ev: Event) {
			const me = DOMSH.findHost<UsersMgrBase<any>>(ev.target as Node);
			await me.refresh();
		}
	);

/** Wrapper d'une action qui déclenche un refresh du tree */
class ActionWrapperRefresUsersMgr<C extends UsersMgrBase<any>> extends ActionWrapper<C> {
	constructor(public usersMgr: UsersMgrBase<any>) {
		super();
	}

	isEnabled(ctx: C): boolean {
		if (ctx.dirtyStatus !== false) return false;
		return super.isEnabled(ctx);
	}

	async execute(ctx: C, ev?: Event): Promise<any> {
		let result = await super.execute(ctx, ev);
		if (result) await this.usersMgr._refresh();
		if (typeof result === "object") {
			let user = result as JUser;
			this.usersMgr.usersGridElt.selectByJUser([user]);
		}
		return result;
	}
}

/** Formulaire : enregistrement des propriétés d'un utilisateur */
class UserFormSaveAction<E extends UsersMgrBase<any>> extends UsersAction<E> {
	static SINGLETON_user = new UserFormSaveAction(EUserType.user);
	static SINGLETON_group = new UserFormSaveAction(EUserType.group);

	constructor(public userType: EUserType, id?: string) {
		super(id || "userFormSave");
		this._label = "Enregistrer";
		this.setGroup("edit");
		//this.setMode("mono");
		this.setIcon("/@skin@/core/dialogs/save.svg");
		this.requireVisiblePerm(this.userType === EUserType.user ? "action.users#update.user" : "action.users#update.group");
	}

	isEnabled(ctx: E): boolean {
		if (!ctx.formUpdateElt || ctx.formUpdateElt.hasAttribute("disabled"))
			return false;
		if (!ctx.dirtyStatus)
			return false;
		return super.isEnabled(ctx);
	}

	isVisible(ctx: E): boolean {
		/*if (!ctx.dirtyStatus)
			return false;*/
		if (!ctx.usersSrv.hasAspect(EUserAspects.updatable)) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: E, ev?: Event): Promise<void> {
		if (ctx.formUpdateElt.reportValidity()) {
			let msg = new MsgOver();
			await msg.setCustomMsg("Veuillez patienter...", "info").showMsgOver(ctx).waitFor(
				new Promise(async (resolve, refuse) => {
					try {
						const params = Object.assign(FORMS.formToJson(ctx.formUpdateElt), ctx.forcedUserDataOnSave);
						if (ctx.users && ctx.users.length == 1) {
							const resp = this.userType === EUserType.user ?
								await ctx.userSelfSrv.updateUser(params) :
								await ctx.userSelfSrv.updateGroup(params);
							let errorStr = USERSELF.tcheckUserSelfRespError(resp, "");
							if (errorStr === null) {
								await ctx.initFormUi();
								// clean des props au juste enregistré (expurgé des props de drive du save, avec les props éventuellement normalisées, ...)
								let savedProps = await ctx.usersSrv.getUser(params.account);
								resolve(ctx.onUserChange(savedProps));
							} else
								refuse(ERROR.show(errorStr ? `L'enregistrement a échoué. ${errorStr}` : "L'enregistrement a échoué.", new Error(JSON.stringify(resp))));
						} else if (ctx.users && ctx.users.length > 1) {
							let countUsers = ctx.users.length;
							let errorsArray: Map<String, String> = new Map();
							for (let ii = 0; ii < countUsers; ii++) {
								let user = ctx.users[ii];
								let posUser = ii + 1;
								msg.setCustomMsg(`Enregistrements en cours (${posUser} sur ${countUsers})...`);
								let userParams: JUser = Object.assign({}, params);
								userParams.account = user.account;
								const resp = this.userType === EUserType.user ?
									await ctx.userSelfSrv.updateUser(userParams) :
									await ctx.userSelfSrv.updateGroup(userParams);
								let errorStr = USERSELF.tcheckUserSelfRespError(resp, "");
								if (errorStr === null) {
									let savedProps = await ctx.usersSrv.getUser(user.account);
									await ctx.onUserChange(savedProps);
								} else
									errorsArray.set(user.account, errorStr);
								await new Promise(resolve => setTimeout(resolve, 500));//FIXME : supprimer si besoin cette tempo justifiée uniquement par le suivi UI
							}
							await ctx.showDetailsUser(null, true);
							if (errorsArray.size == 0) {
								resolve(undefined);
							} else {
								let errorsCount = errorsArray.size;
								let usersList = Array.from(errorsArray.keys()).join(", ");
								refuse(ERROR.show(errorsCount == 1 ? `Une actualisation n'a pu avoir lieu : ${usersList}.` : `${errorsCount} actualisations n'ont pu avoir lieu : ${usersList}.`, new Error(Array.from(errorsArray.values()).join("\n\n"))));
							}
						}
					} catch (e) {
						refuse(ERROR.report(`L'enregistrement a échoué. Veuillez réessayer ultérieurement.`, e));
					}
				})
			);
		}
		//else POPUP.showNotifWarning("Veuillez corriger le formulaire.", ctx);
	}
}

/** Formulaire : reset du formulaire */
class UserFormRevertAction<E extends UsersMgrBase<any>> extends UsersAction<E> {
	static SINGLETON_user = new UserFormRevertAction(EUserType.user);
	static SINGLETON_group = new UserFormRevertAction(EUserType.group);

	constructor(public userType: EUserType, id?: string) {
		super(id || "userFormRevert");
		this._label = "Annuler";
		this.setGroup("edit");
		this.setIcon("/@skin@/core/dialogs/cancel.svg");
		this.requireVisiblePerm(this.userType === EUserType.user ? "action.users#update.user" : "action.users#update.group");
	}

	isEnabled(ctx: E): boolean {
		if (!ctx.formUpdateElt || ctx.formUpdateElt.hasAttribute("disabled"))
			return false;
		if (!ctx.dirtyStatus)
			return false;
		return super.isEnabled(ctx);
	}

	/*isVisible(ctx: E): boolean {
		if (!ctx.dirtyStatus)
			return false;
		return super.isVisible(ctx);
	}*/

	async execute(ctx: UsersMgrBase<any>, ev?: Event): Promise<boolean> {
		// si pas execute issu de "this", confirmation
		if ((ev.target as Element).id === this.getId() || await POPUP.confirm(`Voulez-vous abandonner les modifications en cours ?`, ctx.reg.env.uiRoot, {
			okLbl: "Abandonner les modifications",
			cancelLbl: "Reprendre l'édition"
		})) {
			await ctx.initFormUi(ctx.users.length ? "multi" : "single");
			return ctx.showDetailsUser(ctx.users, true);
		}
	}
}